import { useTinyHost } from "@tiny/host-contract";
import React from "react";

import {
  Checkbox,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Link,
  TruncateTooltip,
  Box,
  OverFlowTip,
  Grid,
  Collapse,
  Chip,
} from "@tiny/components";
import MoreHorizontal from "@tiny/icons/generated/MoreHorizontal";
import ChevronBigDown from "@tiny/icons/generated/ChevronBigDown";
import ChevronBigUp from "@tiny/icons/generated/ChevronBigUp";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { AITrafficShapingView } from "@tiny/api";
import { useTrafficShapingRow } from "./TrafficShapingRow.hooks";
import { TrafficShapingRowProps } from "./TrafficShapingRow.types";
import { useStyles } from "./TrafficShapingRow.styles";
import { dateFormat, warmupTypeLabelMap } from "./TrafficShapingRow.helper";
import ItemActionTimeInfo from "@tiny/components/src/ItemActionTimeInfo/ItemActionTimeInfo";

function TrafficShapingRow(props: TrafficShapingRowProps) {
  const { navigate } = useTinyHost();
  const { ai_traffic_shaping_item } = props;
  const { mdUp } = useBreakpoints();
  const classes = useStyles();

  const {
    onSelect,
    disabledCheckbox,
    disabledCheckboxToolTip,
    moreActions,
    moreButtonRef,
    checked,
    mutationLoading,
    showMore,
    hideMore,
    moreOpen,
    detailsOpen,
    toggleDetails,
    timeActions,
    sortAction,
  } = useTrafficShapingRow(props);

  const trafficShapingName: React.ReactElement = mdUp ? (
    <TruncateTooltip arrow>
      <Typography
        variant="label1"
        mr={2}
        className={classes.name}
        noWrap
        color="primary"
        tone="main"
      >
        {ai_traffic_shaping_item.name}
      </Typography>
    </TruncateTooltip>
  ) : (
    <Typography
      variant="h4"
      mr={2}
      color="primary"
      tone="main"
      component="span"
    >
      {ai_traffic_shaping_item.name && (
        <OverFlowTip text={ai_traffic_shaping_item.name} />
      )}
    </Typography>
  );

  const renderTrafficShapingName = () => {
    return (
      <Link
        component={"button" as React.ElementType}
        onClick={() =>
          navigate.to(
            `/main/ai-traffic-control?id=${ai_traffic_shaping_item.id}`
          )
        }
      >
        {trafficShapingName}
      </Link>
    );
  };

  const showRunning =
    ai_traffic_shaping_item.assignedToCampaigns &&
    !ai_traffic_shaping_item.completed;
  const showCompleted = ai_traffic_shaping_item.completed;
  const showRecurring = ai_traffic_shaping_item.recurring;
  const showIteration =
    showRecurring &&
    ai_traffic_shaping_item?.recurringIteration &&
    ai_traffic_shaping_item?.recurringIteration > 0;

  return (
    <li className={classes.root}>
      <div className={classes.top}>
        <div className={classes.check}>
          <Checkbox
            color="primary"
            checked={checked}
            onChangeValue={onSelect}
            disabled={disabledCheckbox}
            disabledTooltip={disabledCheckboxToolTip}
          />
        </div>
        <div className={classes.main}>
          {mdUp ? (
            <Box>
              {renderTrafficShapingName()}
              <Box display={"flex"} mt={1} alignItems={"center"}>
                {showRunning && (
                  <Chip label="Running" color="primary" size="small" />
                )}
                {showCompleted && (
                  <Chip label="Completed" color="purple" size="small" />
                )}
                {showRecurring && (
                  <Chip label="Recurring" color="primary" size="small" />
                )}
                {!!showIteration && (
                  <Chip
                    label={`Iteration : ${ai_traffic_shaping_item?.recurringIteration}`}
                    color="yellow"
                    textColor="black"
                    size="small"
                  />
                )}
                <ItemActionTimeInfo
                  actions={timeActions}
                  dateFormat={dateFormat}
                  highlight={sortAction}
                  display="inline"
                />
              </Box>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column">
              <Box
                minWidth={0}
                display="flex"
                flexDirection="column"
                height={20}
                position="relative"
              >
                {renderTrafficShapingName()}
              </Box>
              <ItemActionTimeInfo
                actions={timeActions}
                dateFormat={dateFormat}
                highlight={sortAction}
                display="inline"
              />
              <Box
                minWidth={0}
                display="flex"
                flexDirection="row"
                alignItems="center"
              >
                <IconButton
                  onClick={toggleDetails}
                  className={classes.detailsButton}
                  size="large"
                >
                  {detailsOpen ? <ChevronBigUp /> : <ChevronBigDown />}
                </IconButton>
              </Box>
            </Box>
          )}
        </div>
        {mdUp && (
          <div className={classes.details}>
            <Details item={ai_traffic_shaping_item} showCreatedAt />
          </div>
        )}
        <div className={classes.more}>
          {moreActions.length > 0 && (
            <IconButton
              ref={moreButtonRef}
              className={classes.moreButton}
              onClick={showMore}
              disabled={disabledCheckbox}
              size="large"
            >
              <MoreHorizontal />
            </IconButton>
          )}
          <Menu
            anchorEl={moreButtonRef.current}
            keepMounted
            open={moreOpen}
            onClose={hideMore}
            disableScrollLock
          >
            {moreActions.map((action) => (
              <MenuItem
                key={action.name}
                onClick={() => {
                  hideMore();
                  action.onClickAction(ai_traffic_shaping_item.id!);
                }}
                disabled={mutationLoading}
              >
                {action.name}
              </MenuItem>
            ))}
          </Menu>
        </div>
      </div>
      {!mdUp && (
        <Collapse in={detailsOpen}>
          <div className={classes.bottom}>
            <Details item={ai_traffic_shaping_item} showCreatedAt />
          </div>
        </Collapse>
      )}
    </li>
  );
}

export interface DetailsProps {
  item: AITrafficShapingView;
  showCreatedAt?: boolean;
}

function Details({ item }: DetailsProps) {
  const { warmupType } = item;
  return (
    <Grid container verticalSpacing={3}>
      <Grid item xs={6}>
        <Typography
          variant="body2"
          color="grey"
          tone={900}
          component="div"
          mb={1}
        >
          {warmupTypeLabelMap[warmupType] || "-"}
        </Typography>
        <Typography variant="label3" color="grey" tone={600} component="div">
          Warmup Type
        </Typography>
      </Grid>
    </Grid>
  );
}

export default React.memo(TrafficShapingRow);
